import { ActivityType } from '../../../../pure-js/enums/ActivityType'
import type {
  FacilitatorLiveSessionParticipant,
  GetFacilitatorLiveSessionMonitorResponse
} from '../../../../pure-js/types/FacilitatorLiveSessionMonitor'
import type { Session } from '../../../../pure-js/types/Session'
import type { SprintParticipant } from '../../../../pure-js/types/Sprint'
import type { ActivityWithSessions } from '../../types/GeneralTypes'
import { buildSprintTeamDisplayBuckets } from '../../../../pure-js/libs/buildSprintTeamDisplayBuckets'

const toIso = (minutesFromNow: number) => new Date(Date.now() + minutesFromNow * 60_000).toISOString()

const participants: SprintParticipant[] = [
  {
    name: 'Alice Gamer',
    userId: 'mock-user-1',
    teamId: 'mock-team-alpha'
  },
  {
    name: 'Bob Gamer',
    userId: 'mock-user-2',
    teamId: 'mock-team-alpha'
  },
  {
    name: 'Sarah Johnson',
    userId: 'mock-user-3',
    teamId: 'mock-team-alpha'
  }
]

const mockSprintTeams = [{ name: 'Alpha Team', tempTeamId: 'mock-team-alpha' }]
const { teamDetails: mockTeamDetails, unassignedMemberNames: mockUnassignedMemberNames } =
  buildSprintTeamDisplayBuckets({
    teams: mockSprintTeams,
    participants
  })

const liveGameSession = {
  id: 'mock-session-live-game',
  createdAt: toIso(-18),
  teamId: 'mock-team-alpha',
  activityId: 'mock-activity-live-game',
  sprintId: 'mock-sprint-1',
  status: 'active'
} as Session

const upcomingGameSession = {
  id: 'mock-session-upcoming-game',
  createdAt: toIso(55),
  teamId: 'mock-team-alpha',
  activityId: 'mock-activity-upcoming-game',
  sprintId: 'mock-sprint-1',
  status: 'inactive'
} as Session

export const buildMockUpcomingActivities = (): ActivityWithSessions[] => [
  {
    id: 'mock-activity-live-game',
    title: 'Gaming Session 2',
    description: 'Live team session in progress',
    startDate: toIso(-15),
    endDate: toIso(45),
    endedAt: '',
    completedActivityTeamIds: [],
    emailsSent: [],
    type: ActivityType.SHORT_GAME,
    sprintTitle: 'Mock Sprint Alpha',
    sprintId: 'mock-sprint-1',
    teamIds: ['mock-team-alpha'],
    participants,
    teamDetails: mockTeamDetails,
    unassignedMemberNames: mockUnassignedMemberNames,
    sessions: [liveGameSession]
  },
  {
    id: 'mock-activity-ai-debrief',
    title: 'AI Debrief',
    description: 'Short facilitator reflection',
    startDate: toIso(-3),
    endDate: toIso(30),
    endedAt: '',
    completedActivityTeamIds: [],
    emailsSent: [],
    type: ActivityType.SHORT_REFLECTION,
    sprintTitle: 'Mock Sprint Alpha',
    sprintId: 'mock-sprint-1',
    teamIds: ['mock-team-alpha'],
    participants,
    teamDetails: mockTeamDetails,
    unassignedMemberNames: mockUnassignedMemberNames,
    sessions: []
  },
  {
    id: 'mock-activity-upcoming-game',
    title: 'Gaming Session 3',
    description: 'Upcoming scheduled activity',
    startDate: toIso(60),
    endDate: toIso(120),
    endedAt: '',
    completedActivityTeamIds: [],
    emailsSent: [],
    type: ActivityType.LONG_GAME,
    sprintTitle: 'Mock Sprint Alpha',
    sprintId: 'mock-sprint-1',
    teamIds: ['mock-team-alpha'],
    participants,
    teamDetails: mockTeamDetails,
    unassignedMemberNames: mockUnassignedMemberNames,
    sessions: [upcomingGameSession]
  }
]

// Everyone is in the call in the mock; `played` lists who acted in the current loop.
const toRoster = (names: string[], played: string[]): FacilitatorLiveSessionParticipant[] =>
  names.map((name, index) => ({
    userId: `mock-user-${index}`,
    name,
    inMeeting: true,
    playedThisLoop: played.includes(name)
  }))

export const buildMockFacilitatorLiveSessionMonitor = (): GetFacilitatorLiveSessionMonitorResponse => ({
  success: true,
  generatedAt: new Date().toISOString(),
  sections: [
    {
      sprintId: 'mock-sprint-1',
      sprintTitle: 'Mock Sprint Alpha',
      sprintCalls: [
        {
          cardId: 'mock-sprint-1:mock-activity-introduction:sprint',
          sprintId: 'mock-sprint-1',
          activityId: 'mock-activity-introduction',
          activityType: ActivityType.INTRODUCTION,
          activityTitle: 'Kickoff',
          startDate: toIso(-10),
          endDate: toIso(20),
          participants: [
            {
              userId: 'mock-user-1',
              name: 'Alice Gamer',
              teamName: 'Alpha Team',
              inMeeting: true,
              playedThisLoop: false
            },
            {
              userId: 'mock-user-2',
              name: 'Bob Gamer',
              teamName: 'Alpha Team',
              inMeeting: true,
              playedThisLoop: false
            },
            {
              userId: 'mock-user-3',
              name: 'Sarah Johnson',
              teamName: 'Alpha Team',
              inMeeting: true,
              playedThisLoop: false
            },
            { userId: 'mock-user-4', name: 'Omar Lind', teamName: 'Beta Team', inMeeting: false, playedThisLoop: false }
          ]
        }
      ],
      cards: [
        {
          cardId: 'mock-live-game-card',
          kind: 'game',
          sprintId: 'mock-sprint-1',
          sprintTitle: 'Mock Sprint Alpha',
          activityId: 'mock-activity-live-game',
          activityTitle: 'Gaming Session 2',
          teamId: 'mock-team-alpha',
          teamName: 'Alpha Team',
          startDate: toIso(-15),
          endDate: toIso(45),
          expectedCount: 3,
          playedCount: 2,
          inMeetCount: 3,
          participants: toRoster(
            participants.map((participant) => participant.name),
            ['Alice Gamer', 'Bob Gamer']
          ),
          loopLengthMinutes: 15,
          stageLabel: 'Loop 2/3 • execution',
          currentLoopNumber: 2,
          totalLoops: 3,
          currentPhase: 'execution',
          minuteInLoop: 6,
          loopSummaries: [
            {
              loopId: 'loop-2',
              loopLabel: 'Loop 2',
              displayIndex: 2,
              startedAt: toIso(-12),
              finishedAt: toIso(8),
              hasStarted: true,
              hasFinished: false,
              status: 'in_progress',
              currentPhase: 'execution',
              minuteInLoop: 6,
              participantNames: ['Alice Gamer', 'Bob Gamer'],
              participantCount: 2
            },
            {
              loopId: 'loop-1',
              loopLabel: 'Loop 1',
              displayIndex: 1,
              startedAt: toIso(-28),
              finishedAt: toIso(-18),
              hasStarted: true,
              hasFinished: true,
              status: 'finished',
              currentPhase: 'planning',
              minuteInLoop: 10,
              participantNames: ['Alice Gamer'],
              participantCount: 1
            }
          ]
        },
        {
          cardId: 'mock-ai-debrief-card',
          kind: 'ai_debrief',
          sprintId: 'mock-sprint-1',
          sprintTitle: 'Mock Sprint Alpha',
          activityId: 'mock-activity-ai-debrief',
          activityTitle: 'AI Debrief',
          teamId: 'mock-team-alpha',
          teamName: 'Alpha Team',
          startDate: toIso(-3),
          endDate: toIso(30),
          expectedCount: 3,
          inMeetCount: 3,
          participants: toRoster(
            participants.map((participant) => participant.name),
            []
          ),
          stageLabel: 'Ready for part 1',
          aiCoachSessionId: 'mock-sprint-1_mock-activity-ai-debrief_mock-team-alpha',
          aiCoachStatus: 'available',
          currentPart: 1,
          completedParts: [],
          canJoinAiDebrief: true
        }
      ]
    }
  ]
})
